import axios, {
	InternalAxiosRequestConfig,
	AxiosInstance,
	AxiosResponse,
} from 'axios';
import {
	API_LATENCY,
	INVALID_CSRF_ERROR_CODE,
	API_STATUS_CODES,
} from '../constants';
import {
	IQueuedRequest,
	IApiClientConfig,
	TFulFilledInterceptor,
	TRejectedInterceptor,
	IRequestOptions,
	IApiError,
	IApiResponse,
	TResponseDataDefault,
	IRequestInterceptorConfig,
	TResponseInterceptorConfig,
	IAnalyticsEventDetails,
	IRequestConfig,
	INetworkMetricConfig,
} from '../types';
import { getUrlAndServiceName } from '../utils';
import { getCleanUrlPath } from '../helpers';

export class ApiClient {
	private static _apiQueue: IQueuedRequest[] = [];
	private static _isRequestInProgress = false;
	private _axiosInstance: AxiosInstance;
	private _refreshToken?: () => Promise<void>;
	private static _resourceTimingObserver?: PerformanceObserver;

	private static _requestConfigs = new Map<
		string,
		IRequestConfig & { serviceName: string }
	>();

	constructor({ refreshToken, ...config }: IApiClientConfig) {
		this._axiosInstance = axios.create(config);
		this._refreshToken = refreshToken;
		this._attachNetworkMetricsInterceptor();
	}

	getAxiosInstance = (): AxiosInstance => {
		return this._axiosInstance;
	}

	addRequestInterceptors<Meta extends object = object>(
		resolveInterceptor: TFulFilledInterceptor<IRequestInterceptorConfig<Meta>>,
		rejectInterceptor?: TRejectedInterceptor,
	) {
		this._axiosInstance.interceptors.request.use(
			resolveInterceptor as unknown as TFulFilledInterceptor<InternalAxiosRequestConfig>,
			rejectInterceptor,
		);
	}

	addResponseInterceptors<Meta extends object = object>(
		resolveInterceptor: TFulFilledInterceptor<TResponseInterceptorConfig<Meta>>,
		rejectInterceptor?: TRejectedInterceptor,
	) {
		this._axiosInstance.interceptors.response.use(
			resolveInterceptor as unknown as TFulFilledInterceptor<AxiosResponse>,
			rejectInterceptor,
		);
	}

	async get<Data = TResponseDataDefault>(
		url: string,
		options: IRequestOptions,
	): Promise<IApiResponse<Data>> {
		const requestConfig = {
			method: 'GET',
			url,
			...options,
		};
		return await this._queue(requestConfig);
	}

	async post<Data = TResponseDataDefault>(
		url: string,
		options: IRequestOptions = {},
	): Promise<IApiResponse<Data>> {
		const { isSequential = true, ...axiosConfig } = options;
		const requestConfig = {
			method: 'POST',
			url,
			isSequential,
			...axiosConfig,
		};
		return await this._queue(requestConfig);
	}

	async delete<Data = TResponseDataDefault>(
		url: string,
		options: IRequestOptions = {},
	): Promise<IApiResponse<Data>> {
		const { isSequential = true, ...axiosConfig } = options;
		const requestConfig = {
			method: 'DELETE',
			url,
			isSequential,
			...axiosConfig,
		};
		return await this._queue(requestConfig);
	}

	async patch<Data = TResponseDataDefault>(
		url: string,
		options: IRequestOptions = {},
	): Promise<IApiResponse<Data>> {
		const { isSequential = true, ...axiosConfig } = options;
		const requestConfig = {
			method: 'PATCH',
			url,
			isSequential,
			...axiosConfig,
		};
		return await this._queue(requestConfig);
	}

	async put<Data = TResponseDataDefault>(
		url: string,
		options: IRequestOptions = {},
	): Promise<IApiResponse<Data>> {
		const { isSequential = true, ...axiosConfig } = options;
		const requestConfig = {
			method: 'PUT',
			url,
			isSequential,
			...axiosConfig,
		};
		return await this._queue(requestConfig);
	}

	static registerNetworkMetricsObserver(config: INetworkMetricConfig) {
		try {
			if (!window.PerformanceObserver) return;
			ApiClient._resourceTimingObserver = new PerformanceObserver((list) => {
				list
					.getEntries()
					.filter((entry) => {
						const { pathname } = new URL(entry.name);
						return pathname.startsWith('/apis');
					})
					.forEach((entry) => {
						const { name, duration } = entry as PerformanceResourceTiming;

						const apiUrl = getCleanUrlPath(name);
						const timeToFetch = +(duration / 1000).toFixed(2); // in seconds

						const requestConfig = ApiClient._requestConfigs.get(apiUrl);
						if (requestConfig && timeToFetch > 0) {
							this._sendNetworkEvent(config, requestConfig, {
								name: API_LATENCY,
								additionalData: {
									serviceName: requestConfig?.serviceName,
									url: apiUrl,
									latency: timeToFetch,
								},
							});
						}
					});
			});

			ApiClient._resourceTimingObserver.observe({
				type: 'resource',
				buffered: true,
			});
		} catch (err) {
			console.warn('Performance API not supported!');
		}
	}

	static cleanNetworkMetricsObserver() {
		ApiClient._resourceTimingObserver?.disconnect();
	}

	static getCancelController() {
		const abortController = new AbortController();
		return {
			signal: abortController.signal,
			cancel: () => abortController.abort(),
		};
	}

	static reset() {
		ApiClient._apiQueue = [];
		ApiClient._isRequestInProgress = false;
		ApiClient._requestConfigs.clear();
	}

	//TODO: add CONNECT, OPTIONS, TRACE and HEAD methods

	private _attachNetworkMetricsInterceptor() {
		this.addRequestInterceptors((config) => {
			const { baseURL = '', url = '' } = config;
			const { fullUrl, serviceName } = getUrlAndServiceName(baseURL, url);
			ApiClient._requestConfigs.set(fullUrl, { ...config, serviceName });
			return config;
		});
	}

	private _queue(
		config: IRequestOptions,
	): Promise<IApiResponse<TResponseDataDefault>> {
		if (!config.isSequential) {
			return new Promise((resolve, reject) => {
				this._callInParallel(config, resolve, reject);
			});
		}

		return new Promise((resolve, reject) => {
			ApiClient._apiQueue.push({
				axiosInstance: this._axiosInstance,
				config,
				resolve,
				reject,
			});
			this._processNextRequest();
		});
	}

	private async _processNextRequest() {
		if (ApiClient._apiQueue.length === 0 || ApiClient._isRequestInProgress) {
			return;
		}

		ApiClient._isRequestInProgress = true;
		const { axiosInstance, config, resolve, reject } =
			ApiClient._apiQueue.shift()!;
		try {
			const response = await ApiClient._makeRequest(axiosInstance, config);
			resolve(response);
		} catch (error) {
			await this._generateTokenAndRetry(
				{
					axiosInstance,
					config,
					resolve,
					reject,
				},
				error as IApiError,
			);
		} finally {
			ApiClient._isRequestInProgress = false;
			this._processNextRequest();
		}
	}

	private async _callInParallel(
		config: IRequestOptions,
		resolve: IQueuedRequest['resolve'],
		reject: IQueuedRequest['reject'],
	) {
		try {
			const response = await ApiClient._makeRequest(
				this._axiosInstance,
				config,
			);
			resolve(response);
		} catch (error) {
			this._generateTokenAndRetry(
				{
					axiosInstance: this._axiosInstance,
					config,
					resolve,
					reject,
				},
				error as IApiError,
			);
		}
	}

	private async _generateTokenAndRetry(
		queuedRequest: IQueuedRequest,
		error: IApiError,
	) {
		const { axiosInstance, config, reject, resolve } = queuedRequest;
		try {
			if (
				error.data?.errorCode === INVALID_CSRF_ERROR_CODE &&
				error.config?.method === 'post'
			) {
				const retriedResponse = await ApiClient._makeRequest(
					axiosInstance,
					config,
				);
				resolve(retriedResponse);
			}
			if (
				error.status === API_STATUS_CODES.PRECONDITION_FAILED &&
				this._refreshToken
			) {
				await this._refreshToken();
				const retriedResponse = await ApiClient._makeRequest(
					axiosInstance,
					config,
				);
				resolve(retriedResponse);
			} else {
				if (error.status === API_STATUS_CODES.UNAUTHORIZED) {
					ApiClient.reset();
				}
				reject(error);
			}
		} catch (err) {
			reject(err as IApiError);
		}
	}

	private static _sendNetworkEvent(
		networkMetricConfig: INetworkMetricConfig,
		config: IRequestConfig,
		event: IAnalyticsEventDetails,
	) {
		// skip event if the request does not want to push the event
		if (config.skipEvent?.includes(event.name)) return;
		networkMetricConfig.sendNetworkMetricEvent(event);
	}

	/**
	 *
	 * adds defaults to request config
	 */
	private static _addRequestDefaults(config: IApiClientConfig) {
		config.headers = config.headers ?? {};
		return config;
	}

	private static _makeRequest<Data = TResponseDataDefault>(
		axiosInstance: AxiosInstance,
		config: IApiClientConfig,
	): Promise<AxiosResponse<Data>> {
		return axiosInstance.request<Data>(ApiClient._addRequestDefaults(config));
	}
}
